/**
 * @fileoverview A modal component for viewing and editing the details of a post.
 * It allows users to update post content, platforms, and visibility, or to archive the post.
 */
import React, { useState, useEffect } from 'react';
import { Platform, type Post, type PostVisibility } from '../types';
import { FacebookIcon, InstagramIcon, XIcon, YouTubeIcon, MastodonIcon, ThreadsIcon, BlueskyIcon, TikTokIcon, XCircleIcon, GlobeIcon, UsersIcon, LockClosedIcon, type IconProps } from './icons';
import { PlatformSelector } from './PlatformSelector';

interface PostDetailModalProps {
  post: Post | null; // The post to display, or null if the modal is closed.
  onClose: () => void;
  onUpdatePost: (postId: string, updates: Partial<Pick<Post, 'content' | 'platforms' | 'visibility'>>) => void;
  onArchivePost: (postId: string) => void;
}

// Maps platform enums to their respective icon components.
const platformIcons: { [key in Post['platforms'][number]]: React.FC<IconProps> } = {
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  X: XIcon,
  YouTube: YouTubeIcon,
  Mastodon: MastodonIcon,
  Threads: ThreadsIcon,
  Bluesky: BlueskyIcon,
  TikTok: TikTokIcon,
};

// Provides styled display names for platforms.
const platformDisplay: Record<Platform, React.ReactNode> = {
    [Platform.Facebook]: <span className="font-bold text-[#1877F2]">{Platform.Facebook}</span>,
    [Platform.Instagram]: <span className="font-bold text-[#E1306C]">{Platform.Instagram}</span>,
    [Platform.X]: <span className="font-bold text-black dark:text-white">{Platform.X}</span>,
    [Platform.YouTube]: <span className="font-bold text-[#FF0000]">{Platform.YouTube}</span>,
    [Platform.Mastodon]: <span className="font-bold text-[#6364FF]">{Platform.Mastodon}</span>,
    [Platform.Threads]: <span className="font-bold text-black dark:text-white">{Platform.Threads}</span>,
    [Platform.Bluesky]: <span className="font-bold text-[#007AFF]">{Platform.Bluesky}</span>,
    [Platform.TikTok]: <span className="font-bold text-black dark:text-white">{Platform.TikTok}</span>,
};

// Defines which platforms support visibility settings.
const PLATFORMS_WITH_VISIBILITY: Platform[] = [Platform.Facebook, Platform.X, Platform.Mastodon];

export const PostDetailModal: React.FC<PostDetailModalProps> = ({ post, onClose, onUpdatePost, onArchivePost }) => {
  // State to toggle between view and edit modes.
  const [isEditing, setIsEditing] = useState(false);
  // State for the edited content.
  const [editedContent, setEditedContent] = useState('');
  // State for the edited platforms.
  const [editedPlatforms, setEditedPlatforms] = useState<Platform[]>([]);
  // State for the edited visibility settings.
  const [editedVisibility, setEditedVisibility] = useState<Partial<Record<Platform, PostVisibility>>>({});
  // State to manage the closing animation.
  const [isClosing, setIsClosing] = useState(false);

  // Effect to reset the modal's state when a new post is passed in.
  useEffect(() => {
    if (post) {
      setIsClosing(false);
      setEditedContent(post.content);
      setEditedPlatforms(post.platforms);
      setEditedVisibility(post.visibility || {});
      setIsEditing(false); // Default to view mode.
    }
  }, [post]);

  // Handles the closing of the modal with a fade-out animation.
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200); // Must match the animation duration.
  };

  // Do not render anything if no post is selected.
  if (!post) {
    return null;
  }
  
  // Saves the changes made in edit mode.
  const handleSave = () => {
    if (editedContent.trim() && editedPlatforms.length > 0) {
      onUpdatePost(post.id, { content: editedContent, platforms: editedPlatforms, visibility: editedVisibility });
      setIsEditing(false);
    }
  };

  // Cancels edit mode and reverts any changes.
  const handleCancel = () => {
    setEditedContent(post.content);
    setEditedPlatforms(post.platforms);
    setEditedVisibility(post.visibility || {});
    setIsEditing(false);
  };

  // Archives the post and closes the modal.
  const handleArchive = () => {
    onArchivePost(post.id);
    handleClose();
  };

  // Filters for platforms that have visibility settings to display the relevant UI.
  const platformsWithVisibilitySelected = editedPlatforms.filter(p => PLATFORMS_WITH_VISIBILITY.includes(p));

  return (
    <div 
      className={`fixed inset-0 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={handleClose} // Close modal on overlay click.
      aria-modal="true"
      role="dialog"
    >
      <div 
        className={`bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-lg relative ${isClosing ? 'animate-fade-out-scale' : 'animate-fade-in-scale'}`}
        onClick={e => e.stopPropagation()} // Prevent closing when clicking inside the modal.
      >
        <button 
          onClick={handleClose} 
          className="absolute top-4 right-4 text-slate-400 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white transition-colors"
          aria-label="Close post details"
        >
          <XCircleIcon className="w-8 h-8" />
        </button>

        <div className="flex items-center space-x-3 mb-4">
          <h2 className="text-xl font-bold">{isEditing ? 'Edit' : 'Post Details'}</h2>
           {/* Display platform and visibility info in view mode */}
           {!isEditing && (
            <div className="flex flex-wrap items-center gap-2">
              {post.platforms.map(p => {
                const Icon = platformIcons[p];
                return (
                  <div key={p} className="flex items-center gap-1.5 bg-slate-100 dark:bg-gray-700 rounded-full px-2.5 py-1">
                    <Icon className="w-4 h-4 text-slate-500 dark:text-gray-400" />
                    <span className="text-xs font-medium text-slate-600 dark:text-gray-300">{p}</span>
                  </div>
                );
              })}
              {post.visibility && Object.entries(post.visibility).map(([platform, visibilityValue]) => {
                  if (!visibilityValue) return null;
                  const PlatformIcon = platformIcons[platform as Platform];
                  if (!PlatformIcon) return null;
                  const VisibilityIcon = visibilityValue === 'public' ? GlobeIcon : visibilityValue === 'friends' ? UsersIcon : LockClosedIcon;
                  return (
                      <div key={platform} className="flex items-center gap-1.5 bg-slate-100 dark:bg-gray-700 rounded-full px-2.5 py-1">
                          <PlatformIcon className="w-4 h-4 text-slate-500 dark:text-gray-400" />
                          <VisibilityIcon className="w-4 h-4 text-slate-500 dark:text-gray-400" />
                          <span className="text-xs font-medium capitalize text-slate-600 dark:text-gray-300">{visibilityValue}</span>
                      </div>
                  );
              })}
            </div>
          )}
        </div>

        {/* Media preview */}
        {post.mediaUrl && (
          <div className="mb-4 rounded-lg overflow-hidden bg-slate-200/50 dark:bg-gray-900/50 flex justify-center">
             {post.mediaType === 'image' && (
              <img src={post.mediaUrl} alt="Post media" className="w-auto h-auto max-h-64 object-contain rounded" />
             )}
             {post.mediaType === 'video' && (
              <video src={post.mediaUrl} className="w-auto h-auto max-h-64 object-contain rounded" controls />
             )}
          </div>
        )}

        {/* Conditional rendering for view vs. edit mode */}
        {isEditing ? (
          <div className="space-y-4">
            {/* Edit form */}
            <div>
              <label htmlFor="edit-content" className="block text-sm font-medium text-slate-500 dark:text-gray-400 mb-2">Content</label>
              <textarea
                id="edit-content"
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-36 bg-slate-100 dark:bg-gray-700 text-slate-800 dark:text-gray-200 rounded-lg p-3 border border-slate-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:outline-none transition"
                rows={5}
              />
            </div>
            <div>
              <label htmlFor="edit-platforms" className="block text-sm font-medium text-slate-500 dark:text-gray-400 mb-2">Platforms</label>
               <PlatformSelector selectedPlatforms={editedPlatforms} onSelectionChange={setEditedPlatforms} />
            </div>
            {platformsWithVisibilitySelected.length > 0 && (
              <div className="space-y-4 pt-4 mt-4 border-t border-slate-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-slate-600 dark:text-gray-400 -mb-2">Platform Settings</h3>
                {platformsWithVisibilitySelected.map(platform => {
                  const platformVisibility = editedVisibility[platform] || 'public';
                  return (
                    <div key={platform}>
                      <label htmlFor={`edit-visibility-${platform}`} className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-gray-400 mb-2">
                        {platformDisplay[platform]}
                        <span>Visibility</span>
                      </label>
                      <div className="relative">
                        <select
                          id={`edit-visibility-${platform}`}
                          value={platformVisibility}
                          onChange={(e) => setEditedVisibility(prev => ({ ...prev, [platform]: e.target.value as PostVisibility }))}
                          className="w-full appearance-none bg-slate-100 dark:bg-gray-700 text-slate-800 dark:text-gray-200 rounded-lg p-3 border border-slate-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:outline-none transition pl-10"
                        >
                          <option value="public">Public</option>
                          <option value="friends">Friends</option>
                          <option value="private">Only Me (Private)</option>
                        </select>
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 dark:text-gray-400">
                          {platformVisibility === 'public' && <GlobeIcon className="w-5 h-5" />}
                          {platformVisibility === 'friends' && <UsersIcon className="w-5 h-5" />}
                          {platformVisibility === 'private' && <LockClosedIcon className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ) : (
          // View mode: Display content
          <div className="bg-slate-100 dark:bg-gray-700/50 p-4 rounded-lg max-h-48 overflow-y-auto">
            <p className="text-slate-800 dark:text-gray-200 whitespace-pre-wrap break-words">{post.content}</p>
          </div>
        )}
        
        {/* Modal footer with actions */}
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-between items-center w-full gap-3">
            {isEditing ? (
                <div className="text-sm text-slate-500 dark:text-gray-500 text-center sm:text-left">
                    Editing post scheduled for {post.scheduledAt.toLocaleDateString()}
                </div>
            ) : (
                <div className="text-sm text-slate-500 dark:text-gray-400 text-center sm:text-left">
                    {post.status === 'archived' ? 'Archived' : `Scheduled for: ${post.scheduledAt.toLocaleString([], { dateStyle: 'full', timeStyle: 'short' })}`}
                </div>
            )}
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {isEditing ? (
              // Actions for edit mode
              <>
                <button
                  onClick={handleCancel}
                  className="w-full sm:w-auto bg-slate-500 hover:bg-slate-600 dark:bg-gray-600 dark:hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:bg-orange-400 dark:disabled:bg-orange-800 disabled:cursor-not-allowed"
                  disabled={!editedContent.trim() || editedPlatforms.length === 0}
                >
                  Save Changes
                </button>
              </>
            ) : (
              // Actions for view mode
              <>
                {post.status !== 'archived' && (
                  <button
                    onClick={handleArchive}
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                  >
                    Archive
                  </button>
                )}
                <button
                  onClick={() => setIsEditing(true)}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                >
                  Edit
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {/* CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-out {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes fade-in-scale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fade-out-scale {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(0.95); }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
        .animate-fade-out { animation: fade-out 0.2s ease-in forwards; }
        .animate-fade-in-scale { animation: fade-in-scale 0.2s ease-out forwards; }
        .animate-fade-out-scale { animation: fade-out-scale 0.2s ease-in forwards; }
      `}</style>
    </div>
  );
};